import { Kbd as MantineKbd, KbdProps, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedKbdProps extends KbdProps {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  /** Polymorphic component to render as (e.g., 'a', 'span', Link) */
  component?: any;
  /** Route path when using component prop with React Router Link */
  to?: string;
  /** Standard anchor href for direct links (when component="a") */
  href?: string;
  /** Target for anchor links (when component="a") */
  target?: string;
  /** Rel attribute for anchor links (when component="a") */
  rel?: string;
}

const Kbd = forwardRef<HTMLElement, ThemedKbdProps>(
  ({ gradient = false, gradientVariant = 'primary', children, style, ...rest }, ref) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';

    if (!gradient) {
      return (
        <MantineKbd ref={ref} style={style} {...rest}>
          {children}
        </MantineKbd>
      );
    }

    const gradientConfig = theme.other.gradients[gradientVariant];

    return (
      <MantineKbd
        ref={ref}
        style={{
          backgroundImage: `linear-gradient(135deg, ${gradientConfig.from}20, ${gradientConfig.to}20)`,
          borderColor: gradientConfig.from,
          color: isDark ? 'white' : gradientConfig.from,
          ...style,
        }}
        {...rest}
      >
        {children}
      </MantineKbd>
    );
  }
);

Kbd.displayName = 'Kbd';

export default Kbd;
